<template>
  <div class="activate-compare-page">
    <GlobalInfoBar title="激活列表比对" subtitle="导入已激活列表，与系统中已激活数据做差异比对" />

    <div class="flow-note">
      <strong>比对逻辑：</strong><br>
      ① 上传外部已激活列表（如OPPO方提供的激活数据）<br>
      ② 以导入表为准，根据IMEI号在系统内查询匹配记录及状态<br>
      ③ 比对结果分类：
      <span class="dot green">●</span> 双方一致
      <span class="dot blue">●</span> 仅导入表有（系统无）
      <span class="dot red">●</span> 数据不一致
    </div>

    <GlobalChunk title="上传激活列表" :padding="[20, 20, 20, 20]">
      <el-upload
        class="upload-zone"
        drag
        action="#"
        :auto-upload="false"
        :show-file-list="false"
        :disabled="uploadLoading"
        accept=".xlsx,.xls,.csv"
        :on-change="handleFile"
      >
        <i class="el-icon-refresh upload-icon"></i>
        <div class="upload-title">上传外部已激活列表</div>
        <div class="upload-subtitle">文件需包含 IMEI号 字段，系统将自动比对</div>
        <el-button type="primary" size="small" :loading="uploadLoading" class="upload-btn">选择文件</el-button>
      </el-upload>
    </GlobalChunk>

    <GlobalChunk :title="compareTitle" :padding="[20, 20, 20, 20]" style="margin-top:20px;">
      <GlobalForm
        ref="searchForm"
        :form-item-list="formItemList"
        :inline="true"
        confirmBtnName="搜索"
        @handleConfirm="query"
      >
        <div slot="AddBtn">
          <el-button size="small" type="info" round @click="clearSearch">重置</el-button>
        </div>
      </GlobalForm>

      <div class="summary-row">
        <div class="summary-card match">
          <div class="label">双方一致</div>
          <div class="value">{{ summary.match }}</div>
        </div>
        <div class="summary-card only-import">
          <div class="label">仅导入表有</div>
          <div class="value">{{ summary.onlyImport }}</div>
        </div>
        <div class="summary-card diff">
          <div class="label">数据不一致</div>
          <div class="value">{{ summary.diff }}</div>
        </div>
      </div>

      <div class="compare-actions">
        <el-button
          size="small"
          :class="['filter-btn', activeFilter === 'match' && 'is-active', 'match']"
          @click="filterChange('match')"
        >
          一致（{{ summary.match }}）
        </el-button>
        <el-button
          size="small"
          :class="['filter-btn', activeFilter === 'onlyImport' && 'is-active', 'only-import']"
          @click="filterChange('onlyImport')"
        >
          仅导入（{{ summary.onlyImport }}）
        </el-button>
        <el-button
          size="small"
          :class="['filter-btn', activeFilter === 'diff' && 'is-active', 'diff']"
          @click="filterChange('diff')"
        >
          不一致（{{ summary.diff }}）
        </el-button>
        <el-button size="small" class="export-btn" @click="exportCompareReport">导出比对报告</el-button>
      </div>

      <GlobalTable
        ref="GlobalTable"
        :columns="tableColumns"
        :data="tableData"
        :isIndex="false"
        :isPagination="true"
        :currentPage="curPage"
        :total="total"
        :pageSize="pageSize"
        :columnStyle="compareCellStyle"
        @handleCurrentChange="currentChange"
      >
        <el-table-column slot="sysTime" label="系统激活时间" align="center">
          <template slot-scope="{ row }">
            <span :class="{ muted: !row.sysTime }">{{ row.sysTime || '—（无记录）' }}</span>
          </template>
        </el-table-column>
        <el-table-column slot="impTime" label="导入表激活时间" align="center">
          <template slot-scope="{ row }">
            <span :class="{ muted: !row.impTime }">{{ row.impTime || '—（无记录）' }}</span>
          </template>
        </el-table-column>
        <el-table-column slot="result" label="比对结果" align="center" width="130">
          <template slot-scope="{ row }">
            <el-tag
              :type="getResultTag(row.result).tagType"
              :class="['result-tag', row.result]"
              size="small"
            >
              {{ getResultText(row) }}
            </el-tag>
          </template>
        </el-table-column>
      </GlobalTable>
    </GlobalChunk>
  </div>
</template>

<script>
import { compareDownload, compareImport, compareResult } from '@/utils/request'

export default {
  name: '激活列表比对',
  data() {
    return {
      activeFilter: '',
      uploadLoading: false,
      compareTitle: '比对结果',
      summary: {
        match: 0,
        onlyImport: 0,
        diff: 0
      },
      formItemList: [
        {
          key: 'systemStartDate',
          type: 'date',
          labelName: '系统激活时间:',
          placeholder: '开始日期',
          valueFormat: 'yyyy-MM-dd'
        },
        {
          key: 'systemEndDate',
          type: 'date',
          labelName: '至:',
          placeholder: '结束日期',
          valueFormat: 'yyyy-MM-dd',
          labelWidth: 40
        }
      ],
      tableColumns: [
        { prop: 'imei', label: 'IMEI号' },
        { prop: 'model', label: '机型' },
        { slotName: 'sysTime' },
        { slotName: 'impTime' },
        { slotName: 'result' }
      ],
      resultTagMap: {
        match: { text: '一致', tagType: 'success' },
        only_import: { text: '仅导入表有', tagType: '' },
        diff: { text: '时间不一致', tagType: 'danger' },
        diff2: { text: '机型不一致', tagType: 'danger' }
      },
      tableData: [],
      curPage: 1,
      total: 0,
      pageSize: 20,
      searchParams: {}
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    query(params) {
      this.searchParams = { ...params }
      this.curPage = 1
      this.fetchList()
    },
    clearSearch() {
      this.$refs.searchForm.clear()
      this.searchParams = {}
      this.curPage = 1
      this.fetchList()
    },
    filterChange(type) {
      this.activeFilter = this.activeFilter === type ? '' : type
      this.curPage = 1
      this.fetchList()
    },
    buildCompareParams(withPage = true) {
      const params = {
        compare_result: this.getCompareResultValue(),
        sys_start_time: this.searchParams.systemStartDate || '',
        sys_end_time: this.searchParams.systemEndDate || ''
      }
      if (withPage) {
        params.page = String(this.curPage)
        params.limit = String(this.pageSize)
      }
      return params
    },
    getCompareResultValue() {
      if (this.activeFilter === 'onlyImport') return 'only_import'
      return this.activeFilter || ''
    },
    getResultTag(result) {
      return this.resultTagMap[result] || { text: '--', tagType: 'info' }
    },
    getResultText(row) {
      return row.resultText || this.getResultTag(row.result).text
    },
    fetchList() {
      compareResult(this.buildCompareParams()).then(res => {
        const data = (res && res.data) || []
        const list = Array.isArray(data) ? data : (data.list || [])
        this.tableData = list.map(this.normalizeCompareRow)
        this.total = res.count || data.total || res.total || 0
        this.updateSummary(data)
      }).catch(() => {
        this.tableData = []
        this.total = 0
      })
    },
    getMockData() {
      const all = [
        { imei: '862345678901234', model: 'Find X8 Pro', sysTime: '2026-06-08 14:32', impTime: '2026-06-08 14:32', result: 'match' },
        { imei: '862345678905678', model: 'Reno 12 Pro', sysTime: '2026-06-08 13:15', impTime: '2026-06-08 13:15', result: 'match' },
        { imei: '862345678911111', model: 'Find X8', sysTime: '', impTime: '2026-06-06 09:10', result: 'onlyImport' },
        { imei: '862345678903456', model: 'Find X8', sysTime: '2026-06-08 10:45', impTime: '2026-06-08 11:02', result: 'diff' },
        { imei: '862345678922222', model: 'Reno 12', sysTime: '2026-06-05 15:30', impTime: '2026-06-05 15:30', result: 'diff2' }
      ]
      if (!this.activeFilter) return all
      if (this.activeFilter === 'diff') return all.filter(row => row.result === 'diff' || row.result === 'diff2')
      return all.filter(row => row.result === this.activeFilter)
    },
    compareCellStyle({ row }) {
      if (row.result === 'only_import') return { background: '#f0f9ff' }
      if (row.result === 'diff' || row.result === 'diff2') return { background: '#fff7f7' }
      return {}
    },
    exportCompareReport() {
      compareDownload(this.buildCompareParams(false)).then(res => {
        this.downloadFile(res, `比对结果_${+new Date()}.xls`)
      }).catch(() => {})
    },
    currentChange(page) {
      this.curPage = page
      this.fetchList()
    },
    handleFile(file) {
      if (!file || !file.raw) return
      this.uploadLoading = true
      const formData = new FormData()
      formData.append('file', file.raw)
      compareImport(formData).then(res => {
        this.applyCompareImportResult((res && res.data) || {})
        this.$message({ type: 'success', message: (res && res.msg) || '文件上传成功，比对结果已更新' })
        this.curPage = 1
        this.fetchList()
      }).catch(() => {
        this.$message({ type: 'error', message: '文件上传失败，请稍后重试' })
      }).finally(() => {
        this.uploadLoading = false
      })
    },
    applyCompareImportResult(data) {
      this.summary = {
        match: this.formatNumber(data.match_count || 0),
        onlyImport: this.formatNumber(data.only_import_count || 0),
        diff: this.formatNumber(data.diff_count || 0)
      }
      this.compareTitle = data.import_time ? `比对结果（${data.import_time} 导入）` : '比对结果'
      this.tableData = []
      this.total = data.total || 0
    },
    normalizeCompareRow(row) {
      return {
        ...row,
        imei: this.formatEmpty(row.imei),
        model: this.formatEmpty(row.import_model || row.model),
        sysTime: this.formatEmpty(row.sys_active_time),
        impTime: this.formatEmpty(row.import_active_time),
        result: row.compare_result || '',
        resultText: row.compare_result_text || row.diff_detail || ''
      }
    },
    updateSummary(data) {
      if (data.match_count === undefined && data.only_import_count === undefined && data.diff_count === undefined) return
      this.summary = {
        match: this.formatNumber(data.match_count || 0),
        onlyImport: this.formatNumber(data.only_import_count || 0),
        diff: this.formatNumber(data.diff_count || 0)
      }
    },
    formatEmpty(value) {
      return value === null || value === undefined || value === '' ? '--' : value
    },
    formatNumber(value) {
      const number = Number(value)
      return Number.isNaN(number) ? value : number.toLocaleString()
    },
    downloadFile(res, fileName) {
      const blob = new Blob([res], { type: 'application/vnd.ms-excel;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    }
  }
}
</script>

<style scoped lang="scss">
.activate-compare-page {
  .flow-note {
    margin-bottom: 16px;
    padding: 12px 16px;
    color: #595959;
    font-size: 13px;
    line-height: 1.8;
    background: #f0f5ff;
    border-left: 4px solid #1890ff;
    border-radius: 0 8px 8px 0;

    .dot {
      margin-left: 12px;
    }
  }

  .green {
    color: #52c41a;
  }

  .blue {
    color: #1890ff;
  }

  .red {
    color: #ff4d4f;
  }

  .upload-zone {
    display: block;

    ::v-deep .el-upload {
      width: 100%;
    }

    ::v-deep .el-upload-dragger {
      width: 100%;
      height: auto;
      padding: 36px 0;
      border-radius: 12px;
    }
  }

  .upload-icon {
    color: #1890ff;
    font-size: 36px;
  }

  .upload-title {
    margin-top: 8px;
    color: #555;
    font-size: 14px;
    font-weight: 500;
  }

  .upload-subtitle {
    margin-top: 6px;
    color: #999;
    font-size: 12px;
  }

  .upload-btn {
    margin-top: 12px;
  }

  .summary-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 16px;
  }

  .summary-card {
    padding: 14px;
    text-align: center;
    border-radius: 8px;

    .label {
      font-size: 11px;
    }

    .value {
      margin-top: 4px;
      font-size: 22px;
      font-weight: 700;
    }

    &.match {
      color: #52c41a;
      background: #f6ffed;
    }

    &.only-import {
      color: #1890ff;
      background: #e6f7ff;
    }

    &.diff {
      color: #ff4d4f;
      background: #fff1f0;
    }
  }

  .compare-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 10px;
    flex-wrap: wrap;
  }

  .filter-btn {
    &.match {
      color: #52c41a;
      background: #f6ffed;
      border-color: #b7eb8f;
    }

    &.only-import {
      color: #1890ff;
      background: #e6f7ff;
      border-color: #91d5ff;
    }

    &.diff {
      color: #ff4d4f;
      background: #fff1f0;
      border-color: #ffa39e;
    }

    &.is-active {
      font-weight: 700;
      box-shadow: inset 0 0 0 1px currentColor;
    }
  }

  .export-btn {
    margin-left: auto;
  }

  .muted {
    color: #999;
  }

  .result-tag.onlyImport {
    color: #1890ff;
    background: #e6f7ff;
    border-color: #91d5ff;
  }
}
</style>
